import type { ProjectStage } from './project-stages.js';

export const BADGE_TONE_NAMES = [
  'gray-light',
  'gray-dark',
  'blue-light',
  'blue-dark',
  'green-light',
  'green-dark',
  'amber',
] as const;

export type BadgeToneName = (typeof BADGE_TONE_NAMES)[number];

export interface BadgeToneColors {
  bg: string;
  border: string;
  dot: string;
  fg: string;
}

export const BADGE_TONE_LIGHT_COLORS = {
  'gray-light': { bg: '#f3f4f6', border: '#e2e6ea', dot: '#909aa4', fg: '#434a54' },
  'gray-dark': { bg: '#edeff3', border: '#d4dae3', dot: '#64748b', fg: '#2a3340' },
  'blue-light': { bg: '#eaf4fc', border: '#bfe0f7', dot: '#5aa6dd', fg: '#1d5685' },
  'blue-dark': { bg: '#e7f0f8', border: '#b3d2ea', dot: '#2f6fa8', fg: '#133a5c' },
  'green-light': { bg: '#eef8f2', border: '#c6e9d3', dot: '#67bd8a', fg: '#1d5b3c' },
  'green-dark': { bg: '#e7f1ea', border: '#b0d1bd', dot: '#2c5f3f', fg: '#1d4530' },
  amber: { bg: '#fbf3e6', border: '#f0dcae', dot: '#c0892a', fg: '#7a4d12' },
} as const satisfies Record<BadgeToneName, BadgeToneColors>;

export const PROJECT_STAGE_TONES = {
  pre_filing: 'gray-light',
  filed: 'gray-dark',
  under_review: 'blue-light',
  approved: 'blue-dark',
  permitted: 'green-light',
  under_construction: 'amber',
  completed: 'green-dark',
} as const satisfies Record<ProjectStage, BadgeToneName>;

/**
 * Accent tones: a three-step ramp per hue for surfaces that need to be
 * identifiable at a glance rather than to carry status. `subtle` is a page-level
 * tint, `soft` a fill or border, `solid` the saturated block, and the two `on*`
 * inks are what stays legible on top of `soft` and `solid`.
 */
export const ACCENT_TONE_NAMES = [
  'blue',
  'cyan',
  'violet',
  'magenta',
  'crimson',
  'rust',
  'citron',
] as const;

export type AccentToneName = (typeof ACCENT_TONE_NAMES)[number];

export interface AccentToneColors {
  subtle: string;
  soft: string;
  solid: string;
  onSoft: string;
  onSolid: string;
}

export const ACCENT_TONE_LIGHT_COLORS = {
  // onSoft is a deep ink of the same hue; rust and citron are already dark
  // enough at `solid` to reuse it. White clears 3:1 on every solid but cyan.
  blue: {
    subtle: '#eef7ff',
    soft: '#b5dbfc',
    solid: '#2454f3',
    onSoft: '#132e86',
    onSolid: '#ffffff',
  },
  cyan: {
    subtle: '#f8ffff',
    soft: '#a0e8fb',
    solid: '#27b7e8',
    onSoft: '#06323f',
    onSolid: '#06323f',
  },
  violet: {
    subtle: '#faf9ff',
    soft: '#bfb4f7',
    solid: '#7e60e1',
    onSoft: '#372183',
    onSolid: '#ffffff',
  },
  magenta: {
    subtle: '#fff7fc',
    soft: '#f6b1de',
    solid: '#e94faf',
    onSoft: '#7e1b58',
    onSolid: '#ffffff',
  },
  crimson: {
    subtle: '#fff8f8',
    soft: '#fbb1b6',
    solid: '#e3313b',
    onSoft: '#7e1b20',
    onSolid: '#ffffff',
  },
  rust: {
    subtle: '#fff1eb',
    soft: '#fcc1a4',
    solid: '#ac3516',
    onSoft: '#ac3516',
    onSolid: '#ffffff',
  },
  citron: {
    subtle: '#fcfde0',
    soft: '#eef474',
    solid: '#76741b',
    onSoft: '#5b5a15',
    onSolid: '#ffffff',
  },
} as const satisfies Record<AccentToneName, AccentToneColors>;
